import React, { Component, Suspense } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect, Link } from "react-router-dom";
import axios from "axios";
import update from "immutability-helper";
import Cookies from "universal-cookie";
import { Helmet } from "react-helmet";
import { withTranslation } from "react-i18next";
import i18n from "i18next";
import ReactGA from "react-ga";

// Utils
import Utils from "./classes/utils";
import CartClass from "./classes/cart";

// Components (lazy)
const ScrollToTop = React.lazy(() => import("./components/scroll-to-top"));
const ErrorDiv = React.lazy(() => import("./components/error"));
const NahibuFooter = React.lazy(() => import("./components/footer"));
const NahibuNavbar = React.lazy(() => import("./components/navbar"));
const RouteChangeTracker = React.lazy(() => import("./components/route-change-tracker"));
const CookieConsent = React.lazy(() => import("./components/cookie-consent"));
const EventBanner = React.lazy(() => import("./components/event-banner"));

// Routes (lazy)
const Pudp = React.lazy(() => import("./routes/pudp"));
const Cgv = React.lazy(() => import("./routes/cgv"));
const CookiePolicy = React.lazy(() => import("./routes/cookie-policy"));
const Affilies = React.lazy(() => import("./routes/affilies"));
const Orders = React.lazy(() => import("./routes/orders"));
const Cart = React.lazy(() => import("./routes/cart"));
const CgvDistributeurs = React.lazy(() => import("./routes/cgv-distributeurs"));
const Order = React.lazy(() => import("./routes/order"));
const Consent = React.lazy(() => import("./routes/consent"));
const LegalNotice = React.lazy(() => import("./routes/legal-notice"));
const ExpertiseNahibu = React.lazy(() => import("./routes/expertise-nahibu"));
const Home = React.lazy(() => import("./routes/home"));
const AffiliatesAccount = React.lazy(() => import("./routes/affiliates-account"));
const Login = React.lazy(() => import("./routes/login"));
const Contact = React.lazy(() => import("./routes/contact"));
const FAQ = React.lazy(() => import("./routes/faq"));
const PasswordReset = React.lazy(() => import("./routes/password-reset"));
const Error404 = React.lazy(() => import("./routes/404"));
const Account = React.lazy(() => import("./routes/account"));
const AccountDataAccess = React.lazy(() => import("./routes/account-data-access"));
const KitDetails = React.lazy(() => import("./routes/kit-details"));
const ProductsPage = React.lazy(() => import("./routes/product-page"));
const OrderValidation = React.lazy(() => import("./routes/order-validation"));

class Routes extends Component {
  constructor(props) {
    super(props);
    const cookies = new Cookies();
    const userCookie = cookies.get("user");

    this.state = {
      error: false,
      message: null,
      redirect: null,
      i18nReady: i18n.isInitialized,
      cart: { items: [], coupons: [], shippingAndTaxes: {}, waiting: true },
      config: {
        apiBaseUrl: window.config ? window.config.wordpressUrl : "https://wp.dev.nahibu.com",
        wooApiUrl: window.config ? window.config.wooApiUrl : "http://dev.local:5000",
        mybioteUrl: window.config ? window.config.mybioteUrl : "http://dev.local:8000",
        tokenAffiliatesViewer: window.config ? window.config.tokenAffiliatesViewer : "Token",
        cookieDomain: window.config ? window.config.cookieDomain : "dev.local",
        products: window.config.products,
        PRODUCT_SKU: window.config.PRODUCT_SKU,
      },
      store: [],
      shippingAndTaxes: {},
      waiting: true,
      auth: { user: {}, logged: false, waiting: true },
      cookies: {
        displayCookieBanner: false,
        displayCookieModal: false,
        necessary: true,
        performance: false,
        targetting: false,
      },
      displayBanner: false,
    };

    this.utils = new Utils();
    this.toogleBanner = this.toogleBanner.bind(this);

    if (window.config?.discountEvent) {
      const { startDate, endDate, labelDesktop, labelMobile } = window.config.discountEvent;
      this.state.config.discountEvent = {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        labelDesktop,
        labelMobile,
      };
    }
  }

  // ===== Helpers Auth & Cookies =====
  getAuthFromCookie = () => {
    const cookies = new Cookies();
    const c = cookies.get("user");
    if (!c || !c.token) return null;
    return {
      id: c.id,
      username: c.username,
      displayName: c.displayName,
      fname: c.fname,
      lname: c.lname,
      email: c.email,
      token: c.token,
    };
  };

  removeUserFromSession() {
    const cookies = new Cookies();
    cookies.remove("user", { path: "/", domain: this.state.config.cookieDomain });
  }

  displayCovidBanner() {
    const cookies = new Cookies();
    const bannerCovidCookie = cookies.get("banner-covid");
    const validCookieBanner = bannerCovidCookie ? !!bannerCovidCookie.display : true;
    if (validCookieBanner) this.setState({ displayBanner: true });
  }

  toogleBanner(event) {
    const cookies = new Cookies();
    cookies.set("banner-covid", { display: !this.state.displayBanner }, { path: "/" });
    this.setState({ displayBanner: !this.state.displayBanner });
    event.preventDefault();
  }

  countCartItems() {
    return Object.values(this.state.cart.items).reduce((sum, item) => sum + item.quantity, 0);
  }

  // ===== Token check =====
  checkToken(callback) {
    const token = this.state.auth.user.token;
    if (!token) return;
    axios
      .post(
        this.state.config.apiBaseUrl + "/wp-json/jwt-auth/v1/token/validate",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        this.setState(
          { auth: update(this.state.auth, { waiting: { $set: false } }) },
          () => typeof callback === "function" && callback()
        );
      })
      .catch((error) => {
        if (error.response?.data?.code === "jwt_auth_invalid_token") {
          this.removeUserFromSession();
          const langSubPath = i18n.language?.startsWith("en")
            ? "/en"
            : i18n.language?.startsWith("es")
              ? "/es"
              : "";
          this.setState({
            redirect: `${langSubPath}/login/`,
            auth: { user: {}, logged: false, waiting: false },
          });
          return;
        } else if (error.request) {
          // réseau
          this.setState({ error: true, message: this.props.t?.("cart|Erreur réseau, veuillez réessayer plus tard") });
        } else {
          // inconnu
          this.setState({ error: true });
        }
      });
  }

  // ===== Cookies Preferences =====
  getCookiePref() {
    const cookies = new Cookies();
    const cookieConsent = cookies.get("cookie-consent");
    if (cookieConsent) {
      const performance = !!cookieConsent.performance;
      const targetting = !!cookieConsent.targetting;
      this.setState({
        cookies: {
          displayCookieBanner: false,
          displayCookieModal: false,
          necessary: true,
          performance,
          targetting,
        },
      });
      if (performance) this.enableGoogleAnalytics();
      if (targetting) this.enableTracking(["facebook", "sendinblue", "gtag-script", "gtag-inline"]);
    } else {
      this.setState({
        cookies: {
          displayCookieBanner: true,
          displayCookieModal: false,
          necessary: true,
          performance: false,
          targetting: false,
        },
      });
    }
  }

  removeCookies = (performance, targetting) => {
    const cookies = new Cookies();
    if (performance) ["_ga", "_gid", "_gat"].forEach((c) => cookies.remove(c));
    if (targetting) ["sib_cuid"].forEach((c) => cookies.remove(c));
  };

  enableGoogleAnalytics = () => {
    const googleAnalyticsId = window.config?.googleAnalyticsId;
    if (googleAnalyticsId) {
      ReactGA.initialize(googleAnalyticsId);
      const url = window.location.pathname + window.location.search;
      ReactGA.set({ page: url });
      ReactGA.pageview(url);
    }
  };

  // Réactive les scripts marqués data-name=... après consentement
  enableTracking = (list) => {
    list.forEach((tracker) => {
      // guillemets dans l’attribut pour éviter toute ambiguïté
      const elements = document.querySelectorAll(`script[data-name="${tracker}"]`);

      elements.forEach((el) => {
        const parent = el.parentElement;
        if (!parent) return;

        const newEl = document.createElement("script");

        // Copier le dataset
        if (el.dataset) {
          Object.keys(el.dataset).forEach((k) => {
            newEl.dataset[k] = el.dataset[k];
          });
        }

        // Copier les autres attributs (sauf src: on le gère via data-src)
        if (typeof el.getAttributeNames === "function") {
          el.getAttributeNames().forEach((attr) => {
            if (attr === "src") return;
            try {
              newEl.setAttribute(attr, el.getAttribute(attr));
            } catch { }
          });
        }

        // Type de script
        newEl.type = (el.dataset && el.dataset.type) || el.type || "text/javascript";

        // Source prioritaire: data-src, fallback éventuel sur src si présent
        const src =
          (el.dataset && el.dataset.src) ||
          el.getAttribute("data-src") ||
          el.getAttribute("src");
        if (src) newEl.src = src;

        // Contenu inline
        if (el.text) newEl.text = el.text;
        else if (el.innerText) newEl.innerText = el.innerText;

        // Remplacement du script
        parent.insertBefore(newEl, el);
        parent.removeChild(el);
      });
    });
  };


  // ===== Store & Cart =====
  async getStore(callback) {
    const requestUrl = window.config.productsDB?.useEmbeddedProductsDB
      ? window.config.productsDB.embeddedProductsDBPath
      : this.state.config.wooApiUrl + "/get-store";
    return axios
      .get(requestUrl)
      .then((response) => {
        const france = response.data.shipping_and_taxes?.countries?.find((c) => c.iso === "FR") || {};
        this.setState(
          {
            waiting: false,
            store: response.data.products,
            shippingAndTaxes: response.data.shipping_and_taxes,
            cart: update(this.state.cart, { shippingAndTaxes: { $set: france } }),
          },
          callback
        );
      })
      .catch(() => this.setState({ error: true }));
  }

  getCookieCart(callback) {
    const cart = new CartClass(this.state.cart, this.props.store);
    cart.getFromCookie();
    this.setState({ cart: cart.toDict() }, callback);
  }

  // ===== Lifecycle =====
  async componentDidMount() {
    this.displayCovidBanner();
    this.getCookiePref();

    // Auth immédiate depuis cookie
    const initialAuth = this.getAuthFromCookie();
    if (initialAuth) {
      this.setState({
        auth: update(this.state.auth, {
          user: { $set: initialAuth },
          logged: { $set: true },
          waiting: { $set: false },
        }),
      });
    } else {
      this.setState({ auth: update(this.state.auth, { waiting: { $set: false } }) });
    }

    // Récupération du store et du panier
    const jobs = [this.getStore()];
    if (initialAuth) {
      jobs.push(new Promise((resolve) => this.checkToken(resolve)));
      jobs.push(new Promise((resolve) => this.getCookieCart(resolve)));
    } else {
      jobs.push(
        new Promise((resolve) =>
          this.getCookieCart(() => {
            this.setState({ cart: update(this.state.cart, { waiting: { $set: false } }) });
            resolve();
          })
        )
      );
    }
    Promise.allSettled(jobs);

    // Intercepteur Axios pour injecter le token dynamique
    if (!this._axiosInterceptor) {
      this._axiosInterceptor = axios.interceptors.request.use((config) => {
        const token = this.state.auth.user?.token || new Cookies().get("user")?.token;
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
    }

    // Écoute des changements du cookie "user"
    this.cookies = new Cookies();
    this.cookieListener = ({ name, value, removed }) => {
      if (name !== "user") return;
      if (removed || !value?.token) {
        this.setState({ auth: { user: {}, logged: false, waiting: false } });
      } else {
        this.setState({
          auth: {
            user: {
              id: value.id,
              username: value.username,
              displayName: value.displayName,
              fname: value.fname,
              lname: value.lname,
              email: value.email,
              token: value.token,
            },
            logged: true,
            waiting: false,
          },
        });
      }
    };
    this.cookies.addChangeListener(this.cookieListener);

    // Redirection domaine ES -> /es/ si besoin
    if (window.location.hostname.includes("nahibu.es") && !window.location.pathname.startsWith("/es/")) {
      const oldPath = window.location.pathname.replace(/^\/(en|fr)\//, "/").replace(/^\/es\//, "/");
      const newPath = "/es" + oldPath;
      i18n.changeLanguage("es");
      window.history.replaceState(null, "", newPath);
    }

    // i18n ready
    if (!this.state.i18nReady) {
      const markReady = () => this.setState({ i18nReady: true });
      i18n.on("initialized", markReady);
      i18n.on("loaded", markReady);
      i18n.on("languageChanged", () => this.forceUpdate());
      if (i18n.isInitialized) this.setState({ i18nReady: true });
    }
  }

  componentWillUnmount() {
    if (this.cookies && this.cookieListener) this.cookies.removeChangeListener(this.cookieListener);
    if (this._axiosInterceptor) axios.interceptors.request.eject(this._axiosInterceptor);
  }

  // ===== Render =====
  render() {
    if (!this.state.i18nReady) {
      return (
        <Router>
          <Suspense fallback={<div></div>}>
            <RouteChangeTracker />
          </Suspense>
          <div className="main-content" style={{ minHeight: "40vh" }} />
        </Router>
      );
    }

    const helmet = (
      <Helmet>
        <meta charSet="utf-8" />
      </Helmet>
    );
    const redirect = this.state.redirect ? <Redirect to={this.state.redirect} /> : null;

    const language = i18n.language?.startsWith("fr")
      ? "fr"
      : i18n.language?.startsWith("en")
        ? "en"
        : "es";
    const langSubPath = language === "en" ? "/en" : language === "es" ? "/es" : "";

    return (
      <Router>
        <Suspense fallback={<div></div>}>
          <RouteChangeTracker />
        </Suspense>
        {helmet}
        {redirect}
        <Suspense fallback={<div></div>}>
          <ScrollToTop />
        </Suspense>
        <Suspense fallback={<div></div>}>
          <NahibuNavbar
            config={this.state.config}
            auth={this.state.auth}
            setStateRoot={(p, cb) => this.setState(p, cb)}
            waitingCart={this.state.cart.waiting}
            cartNumber={this.countCartItems()}
          />
        </Suspense>

        <div className="page-wrapper">
          {this.state.config.discountEvent && (
            <Suspense fallback={<div></div>}>
              <Link to={`${langSubPath}/details-produit/kit-nahibu/`}>
                <EventBanner
                  cssclass="desktop-only bg-dark-blue"
                  position={80}
                  startDate={this.state.config.discountEvent.startDate}
                  endDate={this.state.config.discountEvent.endDate}
                  content={this.state.config.discountEvent.labelDesktop[language]}
                />
                <EventBanner
                  cssclass="mobile-only bg-dark-blue"
                  position=""
                  content={this.state.config.discountEvent.labelMobile[language]}
                />
              </Link>
            </Suspense>
          )}

          <main className="main-content">
            <Switch>
              <Route
                path={"/:locale(es|en|fr)?"}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Home auth={this.state.auth} config={this.state.config} />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/expertise-nahibu/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <ExpertiseNahibu />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/affilies/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Affilies config={this.state.config} />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/orders/"]}
                exact
                component={(props) => (
                  <Suspense fallback={<div></div>}>
                    <Orders
                      {...props}
                      config={this.state.config}
                      auth={this.state.auth}
                      cart={this.state.cart}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/login/"]}
                exact
                component={(props) => (
                  <Suspense fallback={<div></div>}>
                    <Login
                      {...props}
                      getCookieCart={(p) => this.getCookieCart(p)}
                      config={this.state.config}
                      auth={this.state.auth}
                      cart={this.state.cart}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/panier/"]}
                render={(routeProps) => (
                  <Suspense fallback={<div></div>}>
                    <Cart
                      getCookieCart={(p) => this.getCookieCart(p)}
                      waitingStore={this.state.waiting}
                      {...routeProps}
                      auth={this.state.auth}
                      cart={this.state.cart}
                      store={this.state.store}
                      config={this.state.config}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/order/"]}
                render={(routeProps) => (
                  <Suspense fallback={<div></div>}>
                    <Order
                      {...routeProps}
                      auth={this.state.auth}
                      waitingStore={this.state.waiting}
                      cart={this.state.cart}
                      store={this.state.store}
                      shippingAndTaxes={this.state.shippingAndTaxes}
                      config={this.state.config}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                      getCookieCart={(p) => this.getCookieCart(p)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/contact/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Contact config={this.state.config} />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/faq/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <FAQ config={this.state.config} />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/mentions-legales-distributeurs/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <CgvDistributeurs />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/mentions-legales/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <LegalNotice />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/cgv/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Cgv />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/politique-cookie/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <CookiePolicy />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/pudp/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Pudp />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/consentement/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Consent />
                  </Suspense>
                )}
              />
              <Route
                path={[
                  // sans préfixe langue
                  "/compte/", "/account/", "/cuenta/",
                  // avec préfixe langue optionnel
                  "/:locale(es|en|fr)?/compte/",
                  "/:locale(es|en|fr)?/account/",
                  "/:locale(es|en|fr)?/cuenta/",
                ]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Account
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                      config={this.state.config}
                      auth={this.state.auth}
                    />
                  </Suspense>
                )}
              />


              <Route
                path={["/:locale(es|en|fr)?/demande-donnees/"]}
                exact
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <AccountDataAccess
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                      config={this.state.config}
                      auth={this.state.auth}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/password-reset/"]}
                exact
                component={(props) => (
                  <Suspense fallback={<div></div>}>
                    <PasswordReset
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                      config={this.state.config}
                      auth={this.state.auth}
                      {...props}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/compte-affilie/"]}
                exact
                component={(props) => (
                  <Suspense fallback={<div></div>}>
                    <AffiliatesAccount
                      {...props}
                      getCookieCart={(p) => this.getCookieCart(p)}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                      config={this.state.config}
                      auth={this.state.auth}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/details-produit/"]}
                render={(routeProps) => (
                  <Suspense fallback={<div></div>}>
                    <KitDetails
                      {...routeProps}
                      waitingStore={this.state.waiting}
                      config={this.state.config}
                      auth={this.state.auth}
                      product={this.state.product}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/produits/"]}
                render={(routeProps) => (
                  <Suspense fallback={<div></div>}>
                    <ProductsPage
                      {...routeProps}
                      waitingStore={this.state.waiting}
                      config={this.state.config}
                      auth={this.state.auth}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                path={["/:locale(es|en|fr)?/confirmation-commande/"]}
                render={(routeProps) => (
                  <Suspense fallback={<div></div>}>
                    <OrderValidation
                      {...routeProps}
                      config={this.state.config}
                      auth={this.state.auth}
                      cart={this.state.cart}
                      setStateRoot={(p, cb) => this.setState(p, cb)}
                    />
                  </Suspense>
                )}
              />

              <Route
                component={() => (
                  <Suspense fallback={<div></div>}>
                    <Error404 />
                  </Suspense>
                )}
              />
            </Switch>

            <Suspense fallback={<div></div>}>
              <ErrorDiv type="bottom" error={this.state.error} message={this.state.message} />
            </Suspense>
          </main>

          <Suspense fallback={<div></div>}>
            <NahibuFooter config={this.state.config} />
          </Suspense>
        </div>

        <Suspense fallback={<div></div>}>
          <CookieConsent
            removeCookies={this.removeCookies}
            enableTracking={this.enableTracking}
            enableGoogleAnalytics={this.enableGoogleAnalytics}
            debug={window.config ? window.config.debugCookieConsent : false}
            cookies={this.state.cookies}
            setStateRoot={(p, cb) => this.setState(p, cb)}
          />
        </Suspense>
      </Router>
    );
  }
}

export default withTranslation()(Routes);
